import { useMediaQuery } from "~/hooks/useMediaQuery";
import Modal from "../ui/Modal";
import { useCallback, useEffect, useMemo, useState } from "react";
import FlexContainer from "../ui/FlexContainer";
import ProgressBar from "../ui/ProgressBar";
import { useTranslation } from "react-i18next";
import {
  customizationSteps,
  onboardingSteps,
  onboardingStepsMap,
} from "./steps";
import * as S from "./styles";
import Button from "../ui/Button";
import { useAccount } from "~/hooks/useAccount";
import { IOnboardingChoices } from "~/types/account";
import { SVG } from "../ui/SVG";
import { faChevronLeft } from "@fortawesome/pro-solid-svg-icons";
import StaticCatalog from "~/assets/avif/staticCatalog.avif";
import { StoreCreation } from "./store-creation";

import { CustomizationStore } from "./customization-store";
import { useModalTrigger } from "~/hooks/useModalTrigger";
import {
  SHOW_STORE_PROVISIONING_MODAL,
  triggerShowStoreProvisioningModal,
} from "~/helpers/customEvents";
import { sendCustomizationStorePreferences } from "~/api/store/requests";

import Loader from "../ui/Loader";
import { StoreProvisioningModal } from "./store-provisioning-modal";
import { ShowUpgradeModal } from "../upgrade-funnel/showUpgradeModal";

interface Props {
  isShowing: boolean;
  onFinish: () => void;
  onGoToApp: () => void;
}

export const FixedElement = ({
  handleContinue,
}: {
  handleContinue: () => void;
}) => {
  const { t } = useTranslation();
  const isAboveDesktop = useMediaQuery("laptop");

  if (!isAboveDesktop) return null;

  return (
    <S.ButtonFixedContainer>
      <Button
        bgColor="primary"
        color="white"
        width="100%"
        onClick={handleContinue}>
        {t("onboarding.continue")}
      </Button>
    </S.ButtonFixedContainer>
  );
};

export const Onboarding = ({ isShowing, onFinish, onGoToApp }: Props) => {
  const { t } = useTranslation();

  const isAboveDesktop = useMediaQuery("laptopL");
  const shouldAppearProgressBar = useMediaQuery("laptop");
  const savedOnboardingStepIndex = localStorage.getItem("onboardingStepIndex");
  const savedCustomizationStepIndex = localStorage.getItem(
    "customizationStepIndex"
  );
  const [stepIndex, setStepIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { account, refetch: refetchAccount } = useAccount();
  const [onboardingChoices, setOnboardingChoices] = useState<
    IOnboardingChoices | undefined
  >({
    selectedColorScheme: "neutral",
    selectedStoreCategory: "automatic",
    selectedBestDescribedWord: "modern",
    selectedRegion: "global",
  });

  const [isModalOpen] = useModalTrigger(SHOW_STORE_PROVISIONING_MODAL, false);

  const planId = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return queryParams.get("plan_id") || "";
  }, [location.search]);

  useEffect(() => {
    if (!account) return;

    const customizationStore = account.show_store_customization;
    const isFromShopify = account.signup_origin === "shopify";

    if (customizationStore) {
      if (savedCustomizationStepIndex) {
        setStepIndex(parseInt(savedCustomizationStepIndex, 10));
      }
      return;
    }

    if (isFromShopify) {
      return setStepIndex(onboardingStepsMap.SELECT_CATEGORY);
    }

    if (savedOnboardingStepIndex) {
      setStepIndex(parseInt(savedOnboardingStepIndex, 10));
    }
  }, [account]);

  const isStoreCustomization = account?.show_store_customization;

  const steps = isStoreCustomization ? customizationSteps : onboardingSteps;

  if (steps.length === 0 || !account) {
    return null;
  }

  const step = steps[stepIndex];

  const isLastStep = stepIndex === steps.length - 1;
  const isStoreCreationStep = !isStoreCustomization && stepIndex === 0;
  const isCustomizationStep = isStoreCustomization && stepIndex === 0;

  const handleBack = () => {
    if (stepIndex > 0) {
      setStepIndex(stepIndex - 1);
    }
  };

  const handleContinue = useCallback(async () => {
    if (!isLastStep) {
      return setStepIndex(stepIndex + 1);
    }

    localStorage.setItem(
      "onboarding_choices",
      JSON.stringify(onboardingChoices)
    );

    localStorage.setItem("onboardingStepIndex", stepIndex.toString());

    if (isStoreCustomization) {
      localStorage.setItem("customizationStepIndex", stepIndex.toString());

      try {
        setIsSubmitting(true);
        const {
          selectedBestDescribedWord,
          selectedColorScheme,
          selectedRegion,
          selectedStoreCategory,
        } = onboardingChoices!;

        await sendCustomizationStorePreferences({
          selectedBestDescribedWord,
          selectedColorScheme,
          selectedRegion,
          selectedStoreCategory,
        });

        triggerShowStoreProvisioningModal();
        localStorage.removeItem("isOnboarding");
        localStorage.removeItem("customizationStepIndex");
        localStorage.removeItem("onboardingStepIndex");
        refetchAccount();
      } catch (err) {
        console.log("err: ", err);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    onFinish();
  }, [stepIndex, isStoreCustomization]);

  const handleExplore = () => {
    onGoToApp();
  };

  const handleStoreCreationContinue = useCallback(
    async (shouldCreateStore: boolean) => {
      if (shouldCreateStore) {
        localStorage.setItem(
          "onboarding_with_create_store",
          JSON.stringify(shouldCreateStore)
        );
        onFinish();
        return;
      }

      setStepIndex(stepIndex + 1);
    },
    []
  );

  if (isStoreCustomization && planId) {
    return <ShowUpgradeModal open />;
  }

  if (isStoreCreationStep) {
    return <StoreCreation onContinue={handleStoreCreationContinue} />;
  }

  if (isCustomizationStep) {
    return <CustomizationStore onContinue={handleContinue} />;
  }

  if (isModalOpen) {
    return <StoreProvisioningModal onExplore={handleExplore} />;
  }

  const submitButtonText =
    isStoreCustomization && isLastStep
      ? t("onboarding.finish")
      : t("onboarding.continue");

  return (
    <>
      <Modal
        isShowing={isShowing}
        backgroundBlur={4}
        bgImage={StaticCatalog}
        minWidth={isAboveDesktop ? "43%" : "95%"}
        overflow={!isAboveDesktop ? "hidden" : "auto"}
        padding={isAboveDesktop ? "40px" : "20px"}>
        <FlexContainer flexDirection="column" gap="20px">
          <FlexContainer
            flexDirection="column"
            gap="0"
            width="100%"
            alignItems="flex-start"
            data-disabled={isSubmitting}>
            <FlexContainer gap={0.8}>
              {isStoreCreationStep ? null : (
                <Button
                  padding="0"
                  alignSelf="center"
                  onClick={handleBack}
                  bgColor="transparent">
                  <SVG icon={faChevronLeft} color="textSecondary" />
                </Button>
              )}
              <S.StepTitle>{t(step.titleKey)}</S.StepTitle>
            </FlexContainer>
            {shouldAppearProgressBar ? (
              <ProgressBar
                progress={((stepIndex + 1) / steps.length) * 100}
                color="primary"
              />
            ) : null}
          </FlexContainer>

          <step.component
            onboardingChoices={onboardingChoices}
            setOnboardingChoices={setOnboardingChoices}
          />

          <FlexContainer
            justifyContent="space-between"
            flexDirection={isAboveDesktop ? "row" : "column-reverse"}
            width="100%">
            {isAboveDesktop ? (
              <Button
                bgColor="transparent"
                color="textSecondary"
                padding="16px 24px"
                onClick={handleContinue}>
                {t("onboarding.skip")}
              </Button>
            ) : (
              <Button
                bgColor="borderSecondary"
                color="textSecondary"
                width="100%"
                onClick={handleContinue}>
                {t("onboarding.skip")}
              </Button>
            )}

            <FlexContainer width={isAboveDesktop ? "auto" : "100%"}>
              <Button
                bgColor="primary"
                color="white"
                padding="16px 24px"
                width={isAboveDesktop ? "auto" : "100%"}
                onClick={handleContinue}>
                {isSubmitting ? (
                  <S.LoadingContainer>
                    <Loader color="white" isFixed={false} />
                  </S.LoadingContainer>
                ) : (
                  submitButtonText
                )}
              </Button>
            </FlexContainer>
          </FlexContainer>
        </FlexContainer>
      </Modal>
    </>
  );
};
